import { useCompanyConfiguration } from 'init/use-company-configuration';
import { type Reducer, useEffect, useMemo, useReducer, useState } from 'react';
import { omit } from 'remeda';
import { useGetConstants } from 'shared/utils/selectors';
import { reducerState } from 'shared/utils/view';
import type { TableFilter } from 'types/views';

import { HANDLER_SUFFIX, MAX_PREFIX, MIN_PREFIX } from '../filters/filterGrammar';
import type { FilterValues } from '../filters/tableFilterQuery';
import type { SelectOption } from './useFilterOptionsLoader';
import type { FilterResetHandle, FiltersName } from './useTableFilters';

type ConstantOption = { value: string | number; description: string };

// Read a named option list (e.g. 'statuses') off the constants object. Stands in
// for react-hook-form's `get`, which this hook previously used only as a path
// accessor; the cast is the one trust boundary onto the loosely-typed store.
export const readConstantOptions = (source: unknown, path: string): ConstantOption[] => {
  const value = path
    .split('.')
    .reduce<unknown>(
      (accumulator, key) =>
        accumulator && typeof accumulator === 'object'
          ? (accumulator as Record<string, unknown>)[key]
          : undefined,
      source,
    );
  return Array.isArray(value) ? (value as ConstantOption[]) : [];
};

type State = {
  filtersSearched: TableFilter[];
  currentFilter?: TableFilter;
  operator: string;
  searchText: string;
  filters: FilterValues;
};

// The filter categories and their display order. The visible subset is derived
// per table/package (see visibleFilterCategories) — never mutated in place.
const BASE_FILTER_CATEGORIES: Record<string, number> = {
  most_used: 2,
  payment_plan_data: 3,
  task_data: 4,
  invoice_data: 6,
  debtor_data: 8,
  date_data: 10,
  recovery_data: 12,
  custom_data: 14,
};

// Categories hidden per view; everything else in BASE_FILTER_CATEGORIES shows.
// Invoice-family views (invoices/postponable/actionable/credit_notes) fall back to
// the default. custom_data is hidden on top of this whenever the company package
// lacks custom variables.
const HIDDEN_FILTER_CATEGORIES_BY_VIEW: Record<string, string[]> = {
  tasks: ['invoice_data', 'recovery_data', 'custom_data', 'payment_plan_data'],
  debtors: ['invoice_data', 'task_data', 'payment_plan_data'],
  payment_plans: ['task_data', 'invoice_data', 'recovery_data', 'custom_data'],
};
const DEFAULT_HIDDEN_FILTER_CATEGORIES = ['task_data', 'payment_plan_data'];

type UseTableFilterStateOptions = {
  onSubmit: (filters: FilterValues) => void;
  initialValues: FilterValues;
  filterObject: FilterResetHandle;
  tableName: string;
  filtersAvailable: TableFilter[];
  initialFiltersName: FiltersName;
  setFiltersName: (key: string, values: Record<string, string>) => void;
  listData: Record<string, SelectOption[]>;
};

// Owns the filter form's interaction state: the search/edit reducer, the
// per-table visible categories, the react-hook-form instance, and the handlers
// that mutate filters. Presentation lives in TableFilters / FilterList /
// FilterEditor.
export function useTableFilterState({
  onSubmit,
  initialValues,
  filterObject,
  tableName,
  filtersAvailable,
  initialFiltersName,
  setFiltersName,
  listData,
}: UseTableFilterStateOptions) {
  const company = useCompanyConfiguration();
  const constants = useGetConstants();
  const [filtersName, _setFiltersName] = useState(initialFiltersName);

  const [{ filtersSearched, currentFilter, operator, searchText, filters }, setState] = useReducer(
    reducerState as Reducer<State, Partial<State>>,
    {
      filtersSearched: [],
      operator: 'value',
      searchText: '',
      filters: initialValues,
    },
  );

  // Derive the visible categories from the table/package rather than deleting
  // keys off reducer state during render (which mutated state in place).
  const visibleFilterCategories = useMemo<Record<string, number>>(() => {
    const hidden = [
      ...(HIDDEN_FILTER_CATEGORIES_BY_VIEW[tableName] ?? DEFAULT_HIDDEN_FILTER_CATEGORIES),
    ];
    if (!company?.package.can_use_custom_variables) hidden.push('custom_data');
    return omit(BASE_FILTER_CATEGORIES, hidden);
  }, [tableName, company?.package.can_use_custom_variables]);

  // `filters` is the single source of truth (initialised from the URL via
  // initialValues). Applying a change writes it and propagates to the URL — there
  // is no parallel form state. This editor was migrated off react-hook-form.
  const applyFilters = (nextFilters: FilterValues) => {
    setState({ filters: nextFilters });
    onSubmit(nextFilters);
  };

  const setFilterValue = (key: string, value: unknown) =>
    applyFilters({ ...filters, [key]: value });

  useEffect(() => {
    filterObject.reset = () =>
      applyFilters(Object.fromEntries(Object.keys(filters).map(key => [key, ''])));
  }, [filterObject, filters]);

  // filtersAvailable arrives after mount; it must stay in the deps or the derived
  // list never refreshes once the filters load.
  useEffect(() => {
    setState({
      filtersSearched: filtersAvailable.filter(
        filter => filter.name_translated.toLowerCase().indexOf(searchText.toLowerCase()) !== -1,
      ),
    });
  }, [searchText, filtersAvailable]);

  const selectSubmit = (filter: TableFilter) => (selectedValues: string[]) => {
    if (filter.constant_name) {
      const names: Record<string, string> = {};
      readConstantOptions(constants, filter.constant_name).forEach(({ value, description }) => {
        names[value] = description;
      });

      setFiltersName(filter.name, names);
      _setFiltersName({ ...filtersName, [filter.name]: names });
    }

    if (filter.is_api) {
      const names: Record<string, string> = {};
      // The lazy options for this filter may not have loaded yet (e.g. the user
      // hits the MultiSelect clear button before opening it), so guard the lookup.
      (listData[filter.name] ?? []).forEach(item => {
        names[item.id] = item.description;
      });

      setFiltersName(filter.name, names);
      _setFiltersName({ ...filtersName, [filter.name]: names });
    }

    applyFilters({ ...filters, [filter.name]: selectedValues });
  };

  const showFilter = (_currentFilter: TableFilter) => () => {
    if (company?.package.can_use_all_filters || _currentFilter.category === 'most_used') {
      setState({
        currentFilter: _currentFilter,
        // the *_handler key always holds the operator string ('' / null / not_null)
        operator: (initialValues[`${_currentFilter.name}${HANDLER_SUFFIX}`] as string) || 'value',
      });
    }
  };

  // A user operator change clears the filter's value fields and re-submits. This
  // used to live in a [operator] effect that also fired when showFilter set the
  // operator on reopen, silently wiping a saved null/not_null filter (B3).
  const setOperator = (_operator: string) => {
    setState({ operator: _operator });
    if (!currentFilter) return;
    const nextFilters: FilterValues = {
      ...filters,
      [`${currentFilter.name}${HANDLER_SUFFIX}`]: _operator === 'value' ? '' : _operator,
    };
    if (_operator !== 'value') {
      ['', MIN_PREFIX, MAX_PREFIX].forEach(filterPrefix => {
        nextFilters[filterPrefix + currentFilter.name] = '';
      });
    }
    applyFilters(nextFilters);
  };

  const removeCurrentFilter = () => setState({ currentFilter: undefined });

  const setSearchInput = (value: string) => setState({ searchText: value });

  return {
    company,
    constants,
    filters,
    filtersSearched,
    currentFilter,
    operator,
    searchText,
    filtersName,
    visibleFilterCategories,
    setFilterValue,
    selectSubmit,
    showFilter,
    setOperator,
    removeCurrentFilter,
    setSearchInput,
  };
}
